import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import SignaturePad from "./SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONS = [
  { value: "forward", icon: "⚡", labelKey: "pos_forward" },
  { value: "midfielder", icon: "🔄", labelKey: "pos_midfielder" },
  { value: "defender", icon: "🛡️", labelKey: "pos_defender" },
  { value: "goalkeeper", icon: "🧤", labelKey: "pos_goalkeeper" },
];

const FOOT = [
  { value: "right", icon: "🦵", labelKey: "foot_right" },
  { value: "left", icon: "🦵", labelKey: "foot_left", flip: true },
  { value: "both", icon: "⚽", labelKey: "foot_both" },
];

const EXPERIENCE = ["beginner", "intermediate", "advanced", "competitive"];

function calcAge(dob) {
  if (!dob) return "";
  const birth = new Date(dob);
  if (isNaN(birth)) return "";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 0 ? age : "";
}

function todayLocal() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const initialState = {
  player_number: "",
  first_name: "",
  last_name: "",
  date_of_birth: "",
  age: "",
  gender: "",
  position: "",
  dominant_foot: "",
  current_club: "",
  experience: "",
  parent_full_name: "",
  relationship: "",
  email: "",
  phone: "",
  emergency_contact: { name: "", phone: "" },
  has_medical_condition: false,
  medical_condition_details: "",
  allergies: "",
  medications: "",
  insurance: { provider: "", policy_id: "", emergency_phone: "" },  // legacy, not collected anymore
  consent: {
    marketing: false,
    social_media: false,
    insurance_waiver: false,
    liability_waiver: false,
    general: false,
  },
  signature_base64: "",
  guardian_printed_name: "",
  signed_date: todayLocal(),
};

export default function PlayerForm({ onSuccess }) {
  const { t, lang } = useLang();
  const [data, setData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [numberStatus, setNumberStatus] = useState("idle"); // idle|checking|available|taken
  const [serverError, setServerError] = useState("");
  const debounceRef = useRef(null);

  // Refresh signed_date on mount and re-init when component remounts (new registration)
  useEffect(() => {
    setData((d) => ({ ...d, signed_date: todayLocal() }));
  }, []);

  // Fetch suggested number on mount
  useEffect(() => {
    axios
      .get(`${API}/players/next-number`)
      .then((r) => {
        setData((d) => ({ ...d, player_number: r.data.number }));
      })
      .catch(() => {});
  }, []);

  // Debounced uniqueness check
  useEffect(() => {
    if (!data.player_number) {
      setNumberStatus("idle");
      return;
    }
    setNumberStatus("checking");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/players/check-number`, {
          params: { number: data.player_number },
        });
        setNumberStatus(r.data.available ? "available" : "taken");
      } catch {
        setNumberStatus("idle");
      }
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [data.player_number]);

  // Auto-calc age
  useEffect(() => {
    const age = calcAge(data.date_of_birth);
    setData((d) => ({ ...d, age }));
  }, [data.date_of_birth]);

  const set = (field, value) => {
    setData((d) => ({ ...d, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const setNested = (parent, field, value) => {
    setData((d) => ({ ...d, [parent]: { ...d[parent], [field]: value } }));
    setErrors((e) => ({ ...e, [`${parent}.${field}`]: undefined }));
  };

  const validate = useCallback(() => {
    const e = {};
    const required = [
      "player_number",
      "first_name",
      "last_name",
      "date_of_birth",
      "gender",
      "position",
      "dominant_foot",
      "experience",
      "parent_full_name",
      "relationship",
      "email",
      "phone",
      "guardian_printed_name",
    ];
    required.forEach((k) => {
      if (!String(data[k] ?? "").trim()) e[k] = t("required_field");
    });
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      e.email = t("invalid_email");
    }
    if (data.phone && data.phone.replace(/\D/g, "").length < 7) {
      e.phone = t("invalid_phone");
    }
    if (!data.emergency_contact.name.trim())
      e["emergency_contact.name"] = t("required_field");
    if (!data.emergency_contact.phone.trim())
      e["emergency_contact.phone"] = t("required_field");

    if (data.has_medical_condition && !data.medical_condition_details.trim()) {
      e.medical_condition_details = t("required_field");
    }

    const consents = data.consent;
    if (
      !consents.marketing ||
      !consents.social_media ||
      !consents.insurance_waiver ||
      !consents.liability_waiver ||
      !consents.general
    ) {
      e.consent = t("must_accept_all");
    }

    if (!data.signature_base64) e.signature_base64 = t("signature_required");

    if (numberStatus === "taken") e.player_number = t("number_taken");

    return e;
  }, [data, numberStatus, t]);

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      // Scroll to first error
      const first = document.querySelector("[data-error='true']");
      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      // eslint-disable-next-line no-unused-vars
      const { insurance, ...rest } = data;
      const payload = { ...rest, age: Number(data.age) || 0, language: lang };
      const r = await axios.post(`${API}/players`, payload);
      onSuccess(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || t("error_generic");
      setServerError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldErr = (k) => errors[k];
  const inputCls = (k) =>
    `w-full bg-[#0f0f0f] border ${
      fieldErr(k) ? "border-[#e05555]" : "border-[#252525]"
    } rounded-lg px-4 py-3 text-white placeholder:text-neutral-600 focus:border-[#e8559a] focus:ring-2 focus:ring-[#e8559a]/30 focus:outline-none transition-colors`;

  const Label = ({ children, required }) => (
    <label className="block text-sm font-medium text-neutral-300 mb-1.5">
      {children}
      {required && <span className="text-[#e8559a] ml-1">*</span>}
    </label>
  );

  const ErrorMsg = ({ k }) =>
    fieldErr(k) ? (
      <p
        data-error="true"
        data-testid={`error-${k}`}
        className="text-[#e05555] text-xs mt-1.5 flex items-center gap-1"
      >
        <span>⚠</span> {fieldErr(k)}
      </p>
    ) : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-8" noValidate>
      {/* Player number badge */}
      <section className="rounded-2xl bg-[#161616] border border-[#252525] p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row gap-6 items-stretch">
          <div className="flex-shrink-0 flex flex-col items-center justify-center sm:w-44 rounded-xl bg-[#e8559a]/10 border border-[#e8559a]/30 px-4 py-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#e8559a]/80 mb-1">
              # {t("player_number_label")}
            </div>
            <div
              className="font-bebas text-6xl text-[#e8559a] tracking-widest leading-none"
              data-testid="player-number-badge"
            >
              {data.player_number || "—"}
            </div>
            <div className="mt-2 text-[11px]">
              {numberStatus === "checking" && (
                <span className="text-neutral-400">{t("checking")}</span>
              )}
              {numberStatus === "available" && (
                <span className="text-emerald-400" data-testid="number-available">
                  ✓ {t("number_available")}
                </span>
              )}
              {numberStatus === "taken" && (
                <span className="text-[#e05555]" data-testid="number-taken">
                  ✕ {t("number_taken")}
                </span>
              )}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <Label required>{t("player_number_label")}</Label>
            <input
              data-testid="player-number-input"
              value={data.player_number}
              onChange={(e) =>
                set("player_number", e.target.value.replace(/[^0-9]/g, "").slice(0, 4))
              }
              className={inputCls("player_number")}
              placeholder="001"
              inputMode="numeric"
            />
            <p className="text-xs text-neutral-500 mt-1.5">
              {t("player_number_help")}
            </p>
            <ErrorMsg k="player_number" />
          </div>
        </div>
      </section>

      {/* Section 1 - Player */}
      <Section title={t("section_player")} number="1">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label required>{t("first_name")}</Label>
            <input
              data-testid="first-name-input"
              className={inputCls("first_name")}
              value={data.first_name}
              onChange={(e) => set("first_name", e.target.value)}
            />
            <ErrorMsg k="first_name" />
          </div>
          <div>
            <Label required>{t("last_name")}</Label>
            <input
              data-testid="last-name-input"
              className={inputCls("last_name")}
              value={data.last_name}
              onChange={(e) => set("last_name", e.target.value)}
            />
            <ErrorMsg k="last_name" />
          </div>
          <div>
            <Label required>{t("date_of_birth")}</Label>
            <input
              data-testid="dob-input"
              type="date"
              className={inputCls("date_of_birth")}
              value={data.date_of_birth}
              onChange={(e) => set("date_of_birth", e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
            />
            <ErrorMsg k="date_of_birth" />
          </div>
          <div>
            <Label>{t("age")}</Label>
            <input
              data-testid="age-input"
              readOnly
              className={`${inputCls("age")} cursor-not-allowed opacity-80`}
              value={data.age}
              placeholder="—"
            />
          </div>
          <div className="sm:col-span-2">
            <Label required>{t("gender")}</Label>
            <select
              data-testid="gender-select"
              className={inputCls("gender")}
              value={data.gender}
              onChange={(e) => set("gender", e.target.value)}
            >
              <option value="">{t("select")}</option>
              <option value="male">{t("gender_male")}</option>
              <option value="female">{t("gender_female")}</option>
              <option value="other">{t("gender_other")}</option>
            </select>
            <ErrorMsg k="gender" />
          </div>
        </div>

        <div className="mt-6">
          <Label required>{t("primary_position")}</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {POSITIONS.map((p) => {
              const active = data.position === p.value;
              return (
                <button
                  type="button"
                  key={p.value}
                  data-testid={`position-${p.value}`}
                  onClick={() => set("position", p.value)}
                  className={`flex items-center justify-center gap-2 px-3 py-3 rounded-full border transition-all text-sm font-medium ${
                    active
                      ? "bg-[#e8559a]/15 border-[#e8559a] text-[#e8559a] shadow-md shadow-[#e8559a]/20"
                      : "bg-[#0f0f0f] border-[#252525] text-neutral-300 hover:border-[#e8559a]/50"
                  }`}
                >
                  <span>{p.icon}</span>
                  <span>{t(p.labelKey)}</span>
                </button>
              );
            })}
          </div>
          <ErrorMsg k="position" />
        </div>

        <div className="mt-6">
          <Label required>{t("dominant_foot")}</Label>
          <div className="grid grid-cols-3 gap-3">
            {FOOT.map((f) => {
              const active = data.dominant_foot === f.value;
              return (
                <button
                  type="button"
                  key={f.value}
                  data-testid={`foot-${f.value}`}
                  onClick={() => set("dominant_foot", f.value)}
                  className={`flex flex-col items-center justify-center gap-2 p-5 rounded-xl border transition-all ${
                    active
                      ? "bg-[#e8559a]/10 border-[#e8559a] text-[#e8559a] shadow-lg shadow-[#e8559a]/15"
                      : "bg-[#0f0f0f] border-[#252525] text-neutral-300 hover:border-[#e8559a]/50"
                  }`}
                >
                  <span
                    className="text-3xl"
                    style={f.flip ? { transform: "scaleX(-1)" } : undefined}
                  >
                    {f.icon}
                  </span>
                  <span className="text-sm font-medium">{t(f.labelKey)}</span>
                </button>
              );
            })}
          </div>
          <ErrorMsg k="dominant_foot" />
        </div>

        <div className="mt-6">
          <Label>{t("current_club")}</Label>
          <input
            data-testid="club-input"
            className={inputCls("current_club")}
            value={data.current_club}
            onChange={(e) => set("current_club", e.target.value)}
          />
        </div>

        <div className="mt-6">
          <Label required>{t("experience")}</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {EXPERIENCE.map((x) => {
              const active = data.experience === x;
              return (
                <button
                  type="button"
                  key={x}
                  data-testid={`exp-${x}`}
                  onClick={() => set("experience", x)}
                  className={`px-3 py-2.5 rounded-full border text-sm font-medium transition-all ${
                    active
                      ? "bg-[#e8559a]/15 border-[#e8559a] text-[#e8559a]"
                      : "bg-[#0f0f0f] border-[#252525] text-neutral-300 hover:border-[#e8559a]/50"
                  }`}
                >
                  {t(`exp_${x}`)}
                </button>
              );
            })}
          </div>
          <ErrorMsg k="experience" />
        </div>
      </Section>

      {/* Section 2 - Parent */}
      <Section title={t("section_parent")} number="2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <Label required>{t("parent_full_name")}</Label>
            <input
              data-testid="parent-name-input"
              className={inputCls("parent_full_name")}
              value={data.parent_full_name}
              onChange={(e) => set("parent_full_name", e.target.value)}
            />
            <ErrorMsg k="parent_full_name" />
          </div>
          <div>
            <Label required>{t("relationship")}</Label>
            <select
              data-testid="relationship-select"
              className={inputCls("relationship")}
              value={data.relationship}
              onChange={(e) => set("relationship", e.target.value)}
            >
              <option value="">{t("select")}</option>
              <option value="father">{t("rel_father")}</option>
              <option value="mother">{t("rel_mother")}</option>
              <option value="guardian">{t("rel_guardian")}</option>
              <option value="other">{t("rel_other")}</option>
            </select>
            <ErrorMsg k="relationship" />
          </div>
          <div>
            <Label required>{t("email")}</Label>
            <input
              data-testid="email-input"
              type="email"
              className={inputCls("email")}
              value={data.email}
              onChange={(e) => set("email", e.target.value)}
            />
            <ErrorMsg k="email" />
          </div>
          <div className="sm:col-span-2">
            <Label required>{t("phone")}</Label>
            <input
              data-testid="phone-input"
              className={inputCls("phone")}
              value={data.phone}
              placeholder="+1 (303) 555-0123"
              onChange={(e) => set("phone", e.target.value)}
            />
            <ErrorMsg k="phone" />
          </div>
        </div>

        <div className="mt-6">
          <Label required>{t("emergency_contact")}</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 rounded-xl bg-[#0f0f0f] border border-[#252525]">
            <div>
              <input
                data-testid="emergency-name-input"
                placeholder={t("emergency_name")}
                className={inputCls("emergency_contact.name")}
                value={data.emergency_contact.name}
                onChange={(e) =>
                  setNested("emergency_contact", "name", e.target.value)
                }
              />
              <ErrorMsg k="emergency_contact.name" />
            </div>
            <div>
              <input
                data-testid="emergency-phone-input"
                placeholder={t("emergency_phone")}
                className={inputCls("emergency_contact.phone")}
                value={data.emergency_contact.phone}
                onChange={(e) =>
                  setNested("emergency_contact", "phone", e.target.value)
                }
              />
              <ErrorMsg k="emergency_contact.phone" />
            </div>
          </div>
        </div>
      </Section>

      {/* Section 3 - Medical */}
      <Section title={t("section_medical")} number="3">
        <Label>{t("has_medical")}</Label>
        <div className="flex gap-3 mb-5">
          {[
            { v: false, l: t("no") },
            { v: true, l: t("yes") },
          ].map((o) => {
            const active = data.has_medical_condition === o.v;
            return (
              <button
                type="button"
                key={String(o.v)}
                data-testid={`medical-${o.v ? "yes" : "no"}`}
                onClick={() => set("has_medical_condition", o.v)}
                className={`px-6 py-2.5 rounded-full border text-sm font-medium transition-all ${
                  active
                    ? "bg-[#e8559a]/15 border-[#e8559a] text-[#e8559a]"
                    : "bg-[#0f0f0f] border-[#252525] text-neutral-300 hover:border-[#e8559a]/50"
                }`}
              >
                {o.l}
              </button>
            );
          })}
        </div>

        {data.has_medical_condition && (
          <div className="mb-5">
            <Label required>{t("medical_details")}</Label>
            <textarea
              data-testid="medical-details-input"
              rows={3}
              className={inputCls("medical_condition_details")}
              value={data.medical_condition_details}
              onChange={(e) => set("medical_condition_details", e.target.value)}
            />
            <ErrorMsg k="medical_condition_details" />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label>{t("allergies")}</Label>
            <input
              data-testid="allergies-input"
              className={inputCls("allergies")}
              value={data.allergies}
              onChange={(e) => set("allergies", e.target.value)}
            />
          </div>
          <div>
            <Label>{t("medications")}</Label>
            <input
              data-testid="medications-input"
              className={inputCls("medications")}
              value={data.medications}
              onChange={(e) => set("medications", e.target.value)}
            />
          </div>
        </div>
      </Section>

      {/* Section 4 - Consent */}
      <Section title={t("section_consent")} number="4">
        <div className="space-y-3">
          <ConsentRow
            id="marketing"
            icon="📣"
            title={t("consent_marketing_title")}
            text={t("consent_marketing_text")}
            checked={data.consent.marketing}
            onChange={(v) => setNested("consent", "marketing", v)}
          />
          <ConsentRow
            id="social_media"
            icon="📸"
            title={t("consent_social_title")}
            text={t("consent_social_text")}
            checked={data.consent.social_media}
            onChange={(v) => setNested("consent", "social_media", v)}
          />
          <ConsentRow
            id="insurance_waiver"
            icon="🛡️"
            title={t("consent_insurance_title")}
            text={t("consent_insurance_text")}
            checked={data.consent.insurance_waiver}
            onChange={(v) => setNested("consent", "insurance_waiver", v)}
          />
          <ConsentRow
            id="liability_waiver"
            icon="⚠️"
            title={t("consent_liability_title")}
            text={t("consent_liability_text")}
            checked={data.consent.liability_waiver}
            onChange={(v) => setNested("consent", "liability_waiver", v)}
          />
          <ConsentRow
            id="general"
            icon="✅"
            title={t("consent_general_title")}
            text={t("consent_general_text")}
            checked={data.consent.general}
            onChange={(v) => setNested("consent", "general", v)}
          />
        </div>
        {errors.consent && (
          <p
            data-error="true"
            data-testid="error-consent"
            className="text-[#e05555] text-sm mt-3 flex items-center gap-1.5"
          >
            <span>⚠</span> {errors.consent}
          </p>
        )}

        <p className="mt-5 text-xs text-neutral-500 leading-relaxed border-l-2 border-[#e8559a]/40 pl-4">
          {t("legal_note")}
        </p>

        <div className="mt-6">
          <Label required>{t("signature_label")}</Label>
          <SignaturePad
            errored={!!errors.signature_base64}
            onChange={(v) => set("signature_base64", v)}
          />
          <ErrorMsg k="signature_base64" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
          <div>
            <Label required>{t("guardian_printed")}</Label>
            <input
              data-testid="guardian-printed-input"
              className={inputCls("guardian_printed_name")}
              value={data.guardian_printed_name}
              onChange={(e) => set("guardian_printed_name", e.target.value)}
            />
            <ErrorMsg k="guardian_printed_name" />
          </div>
          <div>
            <Label>{t("date_signed")}</Label>
            <input
              data-testid="signed-date-input"
              type="date"
              className={inputCls("signed_date")}
              value={data.signed_date}
              onChange={(e) => set("signed_date", e.target.value)}
              max={todayLocal()}
            />
          </div>
        </div>
      </Section>

      {serverError && (
        <div
          data-testid="server-error"
          className="rounded-xl border border-[#e05555]/40 bg-[#e05555]/10 px-4 py-3 text-[#e05555] text-sm"
        >
          {serverError}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        data-testid="submit-btn"
        className="w-full bg-[#e8559a] hover:bg-[#c9407f] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bebas text-2xl tracking-[0.2em] rounded-xl px-8 py-5 transition-all shadow-lg shadow-[#e8559a]/30"
      >
        {submitting ? t("submitting") : t("submit")} ⚽
      </button>
    </form>
  );
}

function Section({ title, number, children }) {
  return (
    <section className="rounded-2xl bg-[#161616] border border-[#252525] p-5 sm:p-7">
      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#252525]">
        <span className="font-bebas text-2xl text-[#e8559a] bg-[#e8559a]/10 border border-[#e8559a]/30 rounded-md px-2.5 py-0.5 leading-none">
          {number}
        </span>
        <h3 className="font-bebas text-2xl sm:text-3xl text-white tracking-wider">
          {title}
        </h3>
      </div>
      {children}
    </section>
  );
}

function ConsentRow({ id, icon, title, text, checked, onChange }) {
  return (
    <label
      htmlFor={`consent-${id}`}
      data-testid={`consent-${id}-row`}
      className={`flex gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
        checked
          ? "bg-[#e8559a]/[0.06] border-[#e8559a]/50"
          : "bg-[#0f0f0f] border-[#252525] hover:border-[#e8559a]/30"
      }`}
    >
      <input
        id={`consent-${id}`}
        type="checkbox"
        data-testid={`consent-${id}`}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-5 h-5 rounded border-[#252525] bg-[#080808] accent-[#e8559a] flex-shrink-0"
      />
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-base">{icon}</span>
          <span className="font-bebas text-base tracking-wider text-white">
            {title}
          </span>
        </div>
        <p className="text-xs sm:text-sm text-neutral-400 leading-relaxed">
          {text}
        </p>
      </div>
    </label>
  );
}
